import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type React from "react";
import { createContext, useCallback, useContext, useState } from "react";

type ConfirmOptions = {
  title?: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
};

type PromptOptions = {
  title?: string;
  description?: string;
  placeholder?: string;
  defaultValue?: string;
  confirmText?: string;
  cancelText?: string;
};

type DialogContextType = {
  confirm: (options?: ConfirmOptions) => Promise<boolean>;
  prompt: (options?: PromptOptions) => Promise<string | null>;
  alert: (message: string, title?: string) => Promise<void>;
};

const DialogContext = createContext<DialogContextType | null>(null);

export const useDialog = () => {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error("useDialog must be used within DialogProvider");
  return ctx;
};

export const useConfirm = () => {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error("useConfirm must be used within DialogProvider");
  return ctx.confirm;
};

type DialogType = "confirm" | "prompt" | "alert";

export const DialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dialogType, setDialogType] = useState<DialogType>("confirm");
  const [resolvePromise, setResolvePromise] = useState<(val: any) => void>(() => () => {});
  const [confirmOptions, setConfirmOptions] = useState<ConfirmOptions>({});
  const [promptOptions, setPromptOptions] = useState<PromptOptions>({});
  const [inputValue, setInputValue] = useState("");

  const confirm = useCallback((opts: ConfirmOptions = {}) => {
    setDialogType("confirm");
    setConfirmOptions(opts);
    setIsOpen(true);
    return new Promise<boolean>((resolve) => {
      setResolvePromise(() => resolve);
    });
  }, []);

  const prompt = useCallback((opts: PromptOptions = {}) => {
    setDialogType("prompt");
    setPromptOptions(opts);
    setInputValue(opts.defaultValue || "");
    setIsOpen(true);
    return new Promise<string | null>((resolve) => {
      setResolvePromise(() => resolve);
    });
  }, []);

  const alert = useCallback((message: string, title?: string) => {
    setDialogType("alert");
    setConfirmOptions({ title, description: message });
    setIsOpen(true);
    return new Promise<void>((resolve) => {
      setResolvePromise(() => resolve);
    });
  }, []);

  const handleConfirm = () => {
    setIsOpen(false);
    if (dialogType === "confirm") {
      resolvePromise(true);
    } else if (dialogType === "prompt") {
      resolvePromise(inputValue);
    } else if (dialogType === "alert") {
      resolvePromise(undefined);
    } else {
      resolvePromise(undefined);
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
    if (dialogType === "confirm") {
      resolvePromise(false);
    } else if (dialogType === "prompt") {
      resolvePromise(null);
    } else if (dialogType === "alert") {
      resolvePromise(undefined);
    } else {
      resolvePromise(undefined);
    }
  };

  const getTitle = () => {
    if (dialogType === "confirm") return confirmOptions.title || "Are you sure?";
    if (dialogType === "prompt") return promptOptions.title || "Enter value";
    if (dialogType === "alert") return confirmOptions.title || "Alert";
    return "Alert";
  };

  const getDescription = () => {
    if (dialogType === "confirm") return confirmOptions.description;
    if (dialogType === "prompt") return promptOptions.description;
    if (dialogType === "alert") return confirmOptions.description;
    return "Alert";
  };

  const getCancelText = () => {
    if (dialogType === "confirm") return confirmOptions.cancelText || "Cancel";
    if (dialogType === "prompt") return promptOptions.cancelText || "Cancel";
    if (dialogType === "alert") return null;
    return null;
  };

  const getConfirmText = () => {
    if (dialogType === "confirm") return confirmOptions.confirmText || "Yes";
    if (dialogType === "prompt") return promptOptions.confirmText || "OK";
    return "OK";
  };

  return (
    <DialogContext.Provider value={{ confirm, prompt, alert }}>
      {children}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{getTitle()}</DialogTitle>
            {getDescription() && <p>{getDescription()}</p>}
          </DialogHeader>
          {dialogType === "prompt" && (
            <div className="py-4">
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={promptOptions.placeholder}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleConfirm();
                  }
                }}
              />
            </div>
          )}
          <DialogFooter>
            {getCancelText() && (
              <Button variant="outline" onClick={handleCancel}>
                {getCancelText()}
              </Button>
            )}
            <Button onClick={handleConfirm}>{getConfirmText()}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DialogContext.Provider>
  );
};
